import { defaultLicensee } from 'constants/constants';
import { useState } from 'react';
import AuthService from 'services/AuthService';
import CompanyService from 'services/CompanyService';
import { CompanyModel, LicenseeModel } from 'services/EpdClient';

export const useCompany = () => {
  const [companyId, setCompanyId] = useState<string>();
  const [company, setCompany] = useState<CompanyModel>();
  const [companyAccountId, setCompanyAccountId] = useState<string>();
  const [licensee, setLicensee] = useState<LicenseeModel>();

  const fetchCompanyAndLicensees = async (newCompanyId: string) => {
    const newCompany = await CompanyService.getCompany(newCompanyId);

    changeCompanyAccount(newCompany.accounts?.[0]?.id || '');
    setCompanyId(newCompany.id || '');
    window.sessionStorage.setItem('companyId', newCompany.id || '');
    setCompany(newCompany);
    await fetchLicensee(newCompany.id);
  };

  const fetchLicensee = async (newCompanyId?: string) => {
    const newLicensees = await CompanyService.getCompanyLicensees(newCompanyId);
    setLicensee(newLicensees?.[0] ?? defaultLicensee);
  };

  const cleanSessionStorage = () => {
    window.sessionStorage.setItem('companyAccountId', '');
    window.sessionStorage.setItem('companyId', '');
  };

  const setDefaultCompany = () => {
    cleanSessionStorage();
    setCompanyId('');
    setCompany(undefined);
    changeCompanyAccount('');
    setLicensee(defaultLicensee);
  };

  const setLatestOrFirstOrDefaultCompany = async () => {
    cleanSessionStorage();

    const latestCompanyId = AuthService.getUserLatestLoginCompany();
    if (latestCompanyId) {
      try {
        await changeCompany(latestCompanyId);
        return;
      } catch (error) {
        // Among all things, this could happen when a membership is removed
        console.error(`Can't switch to the latest company ${latestCompanyId}, trying first company`, error);
      }
    } else {
      console.warn('The latest company does not exist, trying first company');
    }

    const companiesResult = await CompanyService.getCompanies();
    const firstCompanyId = companiesResult?.[0]?.id;
    if (firstCompanyId) {
      try {
        await changeCompany(firstCompanyId);
        return;
      } catch (error) {
        console.error(`Can't switch to the first company ${firstCompanyId}, trying default company`, error);
      }
    } else {
      console.warn('The first company does not exist, trying default company');
    }

    setDefaultCompany();
  };

  const changeCompany = async (newCompanyId?: string) => {
    if (!newCompanyId || newCompanyId === '') return;

    if (newCompanyId !== companyId) {
      await AuthService.updateUserLatestLoginCompanyId({ companyId: newCompanyId });
    }

    await fetchCompanyAndLicensees(newCompanyId);
  };

  const changeCompanyAccount = (newAccountId?: string) => {
    window.sessionStorage.setItem('companyAccountId', newAccountId || '');
    setCompanyAccountId(newAccountId || '');
  };

  const refreshCompany = async () => {
    if (!companyId || companyId === '') {
      if (!AuthService.isLoggedin()) {
        return;
      }
      const sessionCompanyId = window.sessionStorage.getItem('companyId') || '';
      if (sessionCompanyId) {
        setCompanyAccountId(window.sessionStorage.getItem('companyAccountId') || '');
        try {
          await changeCompany(sessionCompanyId);
          return;
        } catch (error) {
          console.error('Error refreshing company, now falling back to latest/default company', error);
        }
      }
      await setLatestOrFirstOrDefaultCompany();
    }
  };

  return {
    companyId,
    company,
    companyAccountId,
    licensee,
    changeCompany,
    changeCompanyAccount,
    setLatestOrFirstOrDefaultCompany,
    setDefaultCompany,
    refreshCompany,
  };
};
